import { useEffect, useLayoutEffect, useState } from 'react'

/**
 * Motor de paginação real do Sumário — ver o comentário completo em
 * `sections/00b.SumarioSection.js` para o "porquê" (o motor de impressão do Chromium usado
 * por este pipeline via `page.pdf()`/impressão do navegador NÃO suporta
 * `target-counter()`/`string-set` do CSS Paged Media dentro de `page.pdf()` — verificado
 * empiricamente: um `content: target-counter(url(#id), page)` de teste não renderizou NADA,
 * nem o texto estático ao redor dele, num PDF gerado via Playwright/Chromium headless com o
 * mesmo `page.pdf()` deste pipeline). A alternativa aqui é medir a posição real de cada
 * âncora já renderizada e calcular em que página impressa ela cai — só depois disso o
 * Sumário é (re)desenhado com os números reais (segundo "passe").
 *
 * PREMISSA DE LARGURA: esta técnica só funciona porque `components/pdf/Preview.js` já
 * renderiza o documento na tela com a MESMA largura de impressão (`maxWidth: '21cm'`, ou
 * seja, A4) — o texto quebra em linha igual ao impresso, então a altura acumulada na tela
 * corresponde à altura real do conteúdo impresso. Sem essa largura já correspondente, a
 * técnica não funcionaria (o texto quebraria diferente e a altura medida na tela não teria
 * relação com a altura impressa).
 *
 * PREMISSA DE MARGEM/TAMANHO DE PÁGINA: nenhum `@page { size; margin }` é definido em
 * lugar algum deste módulo (nem em `front-react` como um todo) — a margem/tamanho real de
 * impressão é controlado por quem efetivamente chama `page.pdf()`/o diálogo de impressão do
 * navegador, fora deste repositório. `PORTRAIT_RESIDUAL_MARGIN_MM` abaixo é um pequeno
 * resíduo CALIBRADO (não a margem "de olho" de 12mm que o harness de teste declara no seu
 * `@page`) que cobre o que a simulação de blocos atômicos abaixo deliberadamente não
 * modela: a "sobra" de até uma linha de texto no fim de cada página (nenhum motor de
 * impressão quebra uma linha no meio — se a linha inteira não cabe no espaço restante, ela
 * inteira desce para a página seguinte, e simular isso exigiria medir cada `Range`/linha de
 * texto do documento, não só os blocos atômicos) e diferenças menores de caixa/arredondamento
 * não capturadas pela medição do cabeçalho/rodapé. Ver a observação no PR para os números da
 * calibração (harness sintético — sem acesso a backend real neste ambiente, ver a mesma
 * observação para o motivo e a limitação que isso implica).
 *
 * SIMULAÇÃO DE BLOCOS ATÔMICOS (o que mudou nesta revisão): a fonte dominante do erro
 * anterior (17/24 âncoras exatas, resto até 1 página abaixo do real) não era arredondamento
 * — era todo `<table>` deste documento ter `page-break-inside: avoid` por padrão
 * (`components/pdf/Table.js`, nunca sobrescrito por nenhuma seção do PGR Bradesco: ver
 * `sections/_shared/BradescoTable.js`/`MatrizRiscoTable.js` e todo `RedTable` usado nas
 * Seções 1, 5, 6, 9 e 10). Uma tabela assim NUNCA é dividida pelo motor de fragmentação do
 * Chromium: se ela não cabe no espaço restante da página atual mas cabe inteira numa página
 * limpa, o Chromium empurra a tabela INTEIRA para o topo da página seguinte, deixando um vão
 * em branco no fim da página atual — um salto discreto que o cálculo linear antigo (só
 * `posição / altura-da-página`) não tinha como prever, e que cresce (algumas tabelas somem
 * mais espaço, outras menos) conforme mais tabelas "quase cabem" são atravessadas ao longo
 * do documento — exatamente o padrão observado (erro que só cresce, nunca para o lado
 * contrário, e devagar). `getAtomicBlockRanges`/`buildShiftBreakpoints` abaixo simulam esse
 * empurrão: percorrem as tabelas do corpo em ordem, e sempre que uma delas atravessaria uma
 * fronteira de página no cálculo linear, acumulam o deslocamento necessário para empurrá-la
 * para o topo da página seguinte — deslocamento que passa a valer para tudo o que vem depois
 * dela no fluxo (inclusive as âncoras do Sumário). Uma tabela MAIOR que uma página inteira
 * não entra nessa simulação (o Chromium também não consegue respeitar `avoid` nesse caso —
 * deixa dividir, sem deslocamento a compensar).
 *
 * LIMITAÇÃO CONHECIDA E DELIBERADA — SEÇÕES EM PAISAGEM (18/19): a partir da Seção 18
 * (`sections/_shared/Landscape.js`) o documento passa a usar páginas em paisagem — largura
 * impressa maior que a largura de tela usada aqui para medir (`Preview` continua retrato o
 * tempo todo, não é avisada da troca de orientação). A partir daí a premissa de largura
 * equivalente descrita acima deixa de valer, e a altura medida na tela para o conteúdo
 * dentro/depois da paisagem não corresponde de forma confiável à altura impressa. Por isso
 * `SAFE_PAGE_NUMBER_ANCHOR_IDS` (ver `sections/00b.SumarioSection.js`) inclui só as âncoras
 * ANTES da Seção 18 — as entradas do Sumário para as Seções 18-21 ficam sem número de
 * página (não um número adivinhado), pela mesma regra do `SumarioIndexItem`: nunca
 * renderizar um número que não foi de fato calculado com confiança.
 */

const MM_PER_INCH = 25.4
const PX_PER_INCH = 96
const MM_TO_PX = PX_PER_INCH / MM_PER_INCH

const PAGE_HEIGHT_MM = 297 // A4 retrato
const PORTRAIT_NOMINAL_MARGIN_MM = 12 // `@page` declarado pelo harness de impressão (fora deste repositório)
const PORTRAIT_RESIDUAL_MARGIN_MM = 6 // calibrado — ver comentário acima; cobre o que a simulação de blocos atômicos não modela
const PORTRAIT_EFFECTIVE_MARGIN_MM = PORTRAIT_NOMINAL_MARGIN_MM + PORTRAIT_RESIDUAL_MARGIN_MM
const PORTRAIT_PAGE_HEIGHT_PX = (PAGE_HEIGHT_MM - 2 * PORTRAIT_EFFECTIVE_MARGIN_MM) * MM_TO_PX

// Margem de segurança (em px) contra ruído de sub-pixel do `getBoundingClientRect()` ao
// comparar uma posição com uma fronteira de página exata — evita que uma âncora/tabela que
// caia bem em cima da fronteira (diferença de fração de pixel) role para o lado errado.
const PAGE_BOUNDARY_EPSILON_PX = 0.5

// A Capa (`00.CoverSection.js`) é sempre isolada (`pageBreakAfter`) — o conteúdo medido
// aqui (a partir do Sumário) sempre começa na página 2.
const FIRST_BODY_PAGE_NUMBER = 2

// Âncora "zero" da medição: um marcador de altura zero logo no topo do fluxo de conteúdo
// do corpo do documento (ver `PdfDocument.js`, imediatamente antes de `<SumarioSection />`).
export const BODY_ORIGIN_ID = 'pgr-pagination-origin'

// Cabeçalho/rodapé que se repetem em toda página impressa a partir da 2 (ver
// `sections/Header.js` / `sections/Footer.js`) — não se repetem na tela (fluxo único, sem
// paginação real fora da impressão), então sua altura precisa ser descontada manualmente do
// espaço útil de cada página impressa.
export const HEADER_CHROME_ID = 'pgr-header-chrome'
export const FOOTER_CHROME_ID = 'pgr-footer-chrome'

const getElementTop = id => {
  const el = document.getElementById(id)
  return el ? el.getBoundingClientRect().top : null
}

/**
 * Blocos "atômicos" (`page-break-inside: avoid`) dentro do container do corpo, na ordem em
 * que aparecem no fluxo — ver o comentário no topo do arquivo. Descarta uma tabela atômica
 * aninhada dentro de outra tabela atômica (nenhum caso assim existe hoje neste documento;
 * contá-la separadamente duplicaria o deslocamento, já carregado pela tabela externa que a
 * contém). As tabelas de LAYOUT que envolvem todo o corpo (`PdfDocument.js`/
 * `components/pdf/Page.js`) nunca têm `page-break-inside: avoid`, então nunca entram em
 * `avoidTables` — não fazem uma tabela de seção real ser descartada aqui por engano.
 */
const getAtomicBlockRanges = (container, originTop) => {
  const avoidTables = Array.from(container.querySelectorAll('table')).filter(
    table => window.getComputedStyle(table).pageBreakInside === 'avoid'
  )

  return avoidTables
    .filter(table => !avoidTables.some(other => other !== table && other.contains(table)))
    .map(table => {
      const rect = table.getBoundingClientRect()
      return { top: rect.top - originTop, bottom: rect.bottom - originTop }
    })
    .filter(({ top, bottom }) => bottom > top)
    .sort((a, b) => a.top - b.top)
}

/**
 * Simula o empurrão de bloco atômico do motor de fragmentação do Chromium: percorre os
 * blocos em ordem (já deslocados pelo que os blocos anteriores empurraram) e, sempre que um
 * deles atravessaria uma fronteira de página no cálculo linear, registra o deslocamento
 * acumulado a partir da posição bruta daquele bloco — esse deslocamento passa a valer para
 * tudo que vem depois dele no fluxo (ver `shiftAt`).
 */
const buildShiftBreakpoints = (atomicBlocks, effectivePageHeight) => {
  const breakpoints = []
  let shift = 0

  atomicBlocks.forEach(({ top, bottom }) => {
    const printedTop = top + shift
    const printedBottom = bottom + shift
    const blockHeight = printedBottom - printedTop
    // Maior que uma página inteira: o Chromium também não consegue respeitar `avoid` aqui
    // (deixa dividir) — nada a compensar.
    if (blockHeight <= 0 || blockHeight > effectivePageHeight) return

    const startPage = Math.floor(printedTop / effectivePageHeight)
    const endPage = Math.floor((printedBottom - PAGE_BOUNDARY_EPSILON_PX) / effectivePageHeight)
    if (endPage <= startPage) return // não atravessa fronteira de página — nada a empurrar

    shift += (startPage + 1) * effectivePageHeight - printedTop
    breakpoints.push({ rawTop: top, shift })
  })

  return breakpoints
}

// Deslocamento acumulado válido para uma posição bruta (não deslocada) — o último ponto de
// corte cuja posição já foi alcançada no fluxo.
const shiftAt = (breakpoints, rawTop) => {
  let shift = 0
  for (let i = 0; i < breakpoints.length; i++) {
    if (breakpoints[i].rawTop > rawTop) break
    shift = breakpoints[i].shift
  }
  return shift
}

const computePageNumbers = anchorIds => {
  const originEl = document.getElementById(BODY_ORIGIN_ID)
  if (!originEl) return {}
  const originTop = originEl.getBoundingClientRect().top

  const headerHeight = document.getElementById(HEADER_CHROME_ID)?.getBoundingClientRect().height || 0
  const footerHeight = document.getElementById(FOOTER_CHROME_ID)?.getBoundingClientRect().height || 0
  const effectivePageHeight = PORTRAIT_PAGE_HEIGHT_PX - headerHeight - footerHeight
  if (!(effectivePageHeight > 0)) return {}

  const breakpoints = buildShiftBreakpoints(getAtomicBlockRanges(originEl.parentElement, originTop), effectivePageHeight)

  const result = {}
  anchorIds.forEach(id => {
    const top = getElementTop(id)
    if (top === null) return
    const relativeTop = Math.max(0, top - originTop)
    const adjustedTop = relativeTop + shiftAt(breakpoints, relativeTop)
    const pageIndex = Math.floor(adjustedTop / effectivePageHeight + PAGE_BOUNDARY_EPSILON_PX / effectivePageHeight)
    result[id] = FIRST_BODY_PAGE_NUMBER + pageIndex
  })
  return result
}

/**
 * Mede a posição real (após o layout) de cada âncora em `anchorIds` e devolve um mapa
 * `{ [id]: numeroDaPagina }`. IDs cuja âncora não existe/não pôde ser medida simplesmente
 * não aparecem no mapa — nunca um valor "estimado" ou `0` (ver `SumarioIndexItem` em
 * `sections/00b.SumarioSection.js`, que trata a ausência da entrada como "página ainda não
 * conhecida" e não desenha nada, em vez de repetir o bug do `0` fixo corrigido no PR #731).
 *
 * Mede de novo após o `load` da janela: imagens do documento (logo, prints do ERGOCHECK,
 * assinaturas) podem terminar de carregar depois do primeiro layout e mudar a altura do
 * conteúdo — sem isso a primeira medição (antes das imagens carregarem) poderia ficar
 * artificialmente mais "curta" do que o documento final.
 */
export const usePortraitPageNumbers = anchorIds => {
  const [pageNumbers, setPageNumbers] = useState({})
  const anchorIdsKey = anchorIds.join('|')

  useLayoutEffect(() => {
    setPageNumbers(computePageNumbers(anchorIds))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorIdsKey])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const recompute = () => setPageNumbers(computePageNumbers(anchorIds))
    window.addEventListener('load', recompute)
    return () => window.removeEventListener('load', recompute)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorIdsKey])

  return pageNumbers
}
